import { api } from '../api.ts';
import { html, type Raw } from '../html.ts';
import { t, tk } from '../i18n.ts';
import { initReleaseNotices } from '../purple-candidates/site-chrome.js';
import { state } from '../state.ts';
import { openFormDialog, select, textarea, toast } from '../ui.ts';

/**
 * Site chrome shared by every PMA page: the Purple UI release notice and the
 * compact site footer (candidates, see purple-candidates/README.md). The
 * footer's bottom row follows the suite contract: legal line, then imprint,
 * privacy, feedback and the version.
 */

export interface SiteFacts {
  version: string;
  stage: 'beta' | 'preview' | 'stable';
  mail: boolean;
  feedback: boolean;
  operator: string;
  place: string;
  contact: string;
  since: number;
}

let site: SiteFacts | null = null;

/** Loads version, stage and operator facts once; the page renders without them. */
export async function loadSite(): Promise<SiteFacts> {
  if (site) return site;
  const [facts, legal] = await Promise.all([
    api.get<Pick<SiteFacts, 'version' | 'stage' | 'mail' | 'feedback'>>('/site').catch(() => null),
    fetch('/legal/site.json')
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null) as Promise<Partial<SiteFacts> | null>,
  ]);
  site = {
    version: facts?.version ?? '',
    stage: facts?.stage ?? 'beta',
    mail: facts?.mail ?? false,
    feedback: facts?.feedback ?? false,
    operator: legal?.operator ?? '',
    place: legal?.place ?? '',
    contact: legal?.contact ?? '',
    since: legal?.since ?? new Date().getFullYear(),
  };
  return site;
}

function stageLabel(stage: SiteFacts['stage']): string {
  return stage === 'preview' ? t('Vorschau') : stage === 'beta' ? t('Beta') : '';
}

/** Pre-release notice; `bar` at the top of public pages, `card` inside the app. */
export function releaseNotice(placement: 'bar' | 'card' = 'bar'): Raw | '' {
  if (!site || site.stage === 'stable') return '';
  const minor = site.version.split('.').slice(0, 2).join('.');
  return html`<section class="p-release-notice pma-release-notice" data-p-stage="${site.stage}"${placement === 'card' ? html` data-p-placement="card"` : ''}
      data-p-release-notice data-p-dismiss-key="pma-${minor}" aria-label="${t('Hinweis zur Vorabversion')}">
    <span class="p-release-notice__stage">${stageLabel(site.stage)}</span>
    <p class="p-release-notice__text"><strong>${t('Vorabversion.')}</strong> ${t('Funktionen können sich ändern und Daten können verloren gehen. Bitte keine vertraulichen Daten ablegen.')}</p>
    <div class="p-release-notice__actions">
      ${feedbackLink(t('Feedback geben'))}
      <button type="button" class="p-release-notice__dismiss" data-p-release-notice-dismiss aria-label="${t('Hinweis ausblenden')}">×</button>
    </div>
  </section>`;
}

/** Signed in with feedback configured: opens the dialog; otherwise a plain mail link. */
function feedbackLink(label: string): Raw | '' {
  if (!site) return '';
  if (state.me && site.feedback) return html`<a href="/feedback" data-feedback>${label}</a>`;
  return site.contact ? html`<a href="mailto:${site.contact}?subject=${encodeURIComponent('PMA Feedback')}">${label}</a>` : '';
}

export function siteFooter(extraClass = ''): Raw {
  const facts = site;
  const current = location.pathname;
  const legalLine = facts?.operator ? `© ${facts.since} ${facts.operator}${facts.place ? ` · ${t(facts.place)}` : ''}` : '';
  return html`<footer class="p-site-footer pma-site-footer${extraClass ? ` ${extraClass}` : ''}" data-p-variant="compact">
    <div class="p-site-footer__inner"><div class="p-site-footer__bottom">
      <p class="p-site-footer__legal">${legalLine}</p>
      <ul class="p-site-footer__meta" aria-label="${t('Rechtliches und Kontakt')}">
        <li><a href="/impressum"${current === '/impressum' ? html` aria-current="page"` : ''}>${t('Impressum')}</a></li>
        <li><a href="/datenschutz"${current === '/datenschutz' ? html` aria-current="page"` : ''}>${t('Datenschutz')}</a></li>
        ${feedbackLink(t('Feedback')) ? html`<li>${feedbackLink(t('Feedback'))}</li>` : ''}
        ${facts?.version ? html`<li class="p-site-footer__version">${t('Version {version}', { version: facts.version })}${facts.stage !== 'stable' ? ` · ${stageLabel(facts.stage)}` : ''}</li>` : ''}
      </ul>
    </div></div>
  </footer>`;
}

/** Public pages (sign-in, reset, invitation, legal): notice bar, content, footer. */
export function publicPage(content: Raw): Raw {
  return html`<div class="pma-public">${releaseNotice('bar')}<div class="pma-public__content">${content}</div>${siteFooter()}</div>`;
}

const KINDS = [
  ['bug', tk('Fehler')],
  ['idea', tk('Idee')],
  ['question', tk('Frage')],
  ['other', tk('Sonstiges')],
] as const;

export function openFeedbackDialog(): void {
  const page = location.pathname;
  openFormDialog({
    title: t('Feedback geben'),
    description: t('Was funktioniert nicht, was fehlt, was gefällt dir? Die Nachricht geht per E-Mail an das PMA-Team; die Antwort kommt an deine Adresse.'),
    submitLabel: t('Senden'),
    fields: html`${select(
      t('Art'),
      'kind',
      KINDS.map(([value, label]) => `<option value="${value}">${t(label)}</option>`).join(''),
      { required: true },
    )}
      ${textarea(t('Nachricht'), 'message', { required: true, rows: 6, hint: t('Mitgesendet werden dein Name, deine E-Mail-Adresse, die aktuelle Seite ({page}) und die Version.', { page }) })}`,
    onSubmit: async (data) => {
      await api.post('/feedback', { kind: data.kind, message: data.message, page });
      toast(t('Danke für dein Feedback!'), t('Die Nachricht ist unterwegs.'));
    },
  });
}

/** Wires the notice and feedback links inside `root` after it was mounted. */
export function wireChrome(root: ParentNode): void {
  initReleaseNotices(root as Element);
  for (const link of root.querySelectorAll<HTMLAnchorElement>('[data-feedback]')) {
    link.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      openFeedbackDialog();
    });
  }
}
